import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Mail, Phone, MapPin, MessageCircle, Send, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.15 } })
};

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch {
      alert('Failed to send message. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="py-24 sm:py-32 bg-gradient-to-br from-indigo-50 via-white to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <span className="text-xs sm:text-sm tracking-[0.2em] uppercase text-indigo-600 font-semibold">Get in Touch</span>
            <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[1.1] text-slate-900 mt-4 mb-6">
              Let's <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-amber-600">Connect</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Have a question or ready to begin your transformation? Reach out and we will get back to you within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Grid */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              {success ? (
                <div className="bg-emerald-50 rounded-2xl p-10 text-center border border-emerald-100" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-2">Message Sent!</h3>
                  <p className="text-slate-600">Thank you for reaching out. We will get back to you within 24 hours.</p>
                  <Button onClick={() => setSuccess(false)} variant="outline" className="mt-6 rounded-full" data-testid="send-another-btn">
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Full Name</label>
                    <Input placeholder="Your name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="contact-name" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Email</label>
                    <Input type="email" placeholder="your@email.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="contact-email" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Phone (optional)</label>
                    <Input placeholder="+91 98765 43210" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="contact-phone" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Message</label>
                    <Textarea placeholder="How can we help you?" value={form.message} onChange={e => setForm({...form, message: e.target.value})} required rows={5} data-testid="contact-message" />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full rounded-full bg-slate-900 text-white hover:bg-slate-800 py-6 gap-2" data-testid="contact-submit-btn">
                    {submitting ? 'Sending...' : <><Send className="w-4 h-4" /> Send Message</>}
                  </Button>
                </form>
              )}
            </motion.div>

            {/* Info */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} className="space-y-8">
              <div>
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-6">Other Ways to Reach Us</h3>
              </div>

              {[
                { icon: Mail, label: 'Email', value: 'Info@dineshmungath.com', href: 'mailto:Info@dineshmungath.com' },
                { icon: Phone, label: 'Phone', value: '+91 7510792454', href: 'tel:+917510792454' },
                { icon: MessageCircle, label: 'WhatsApp', value: 'Chat with us on WhatsApp', href: 'https://wa.me/917511107924' },
                { icon: MapPin, label: 'Location', value: 'India (Sessions available worldwide via Zoom)', href: null },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 rounded-xl hover:bg-slate-50 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} target="_blank" rel="noopener noreferrer" className="text-slate-900 font-medium hover:text-indigo-600 transition-colors" data-testid={`contact-${item.label.toLowerCase()}`}>
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-slate-900 font-medium">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}

              {/* WhatsApp Button */}
              <a
                href="https://wa.me/917511107924?text=Hi%20Dr.%20Dinesh%2C%20I%27m%20interested%20in%20your%20programs."
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 gap-2" data-testid="whatsapp-btn">
                  <MessageCircle className="w-5 h-5" /> Chat on WhatsApp
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
